import { Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import {
  AlertCircle,
  ClipboardList,
  FileSearch,
  Home,
  LayoutGrid,
  ListChecks,
  Mail,
  Menu,
  PenLine,
  Shapes,
  Star,
  Users,
  UserSquare,
} from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { crossSurfaceLinks } from '@/lib/nav/roleNav';

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

const sidebarGroups = [
  {
    items: [{ to: '/admin', label: 'Overview', icon: Home }],
  },
  {
    label: 'Editorial',
    items: [
      { to: '/admin/content', label: 'Content review', icon: FileSearch },
      { to: '/admin/picks', label: "Editor's picks", icon: Star },
      { to: '/admin/corrections', label: 'Corrections', icon: AlertCircle },
      { to: '/admin/categories', label: 'Categories', icon: LayoutGrid },
      { to: '/admin/symbols', label: 'Symbols', icon: Shapes },
    ],
  },
  {
    // People (+ Menu below) are admin-only — moderators have no server-side
    // capability on these (Spec 21, decision A). Hidden from the moderator
    // sidebar; the routes 403 on direct nav.
    label: 'People',
    items: [
      { to: '/admin/applications', label: 'Applications', icon: ClipboardList, adminOnly: true },
      { to: '/admin/users', label: 'Users', icon: Users, adminOnly: true },
      { to: '/admin/masthead', label: 'Masthead', icon: UserSquare, adminOnly: true },
      { to: '/admin/invites', label: 'Invites', icon: Mail, adminOnly: true },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/admin/copy', label: 'Copy', icon: PenLine, adminOnly: true },
      { to: '/admin/menu', label: 'Menu', icon: Menu, adminOnly: true },
      { to: '/admin/audit', label: 'Audit log', icon: ListChecks },
    ],
  },
];

const bottomNavItems = [
  { to: '/admin', label: 'Overview', icon: Home },
  { to: '/admin/applications', label: 'Apps', icon: ClipboardList, adminOnly: true },
  { to: '/admin/content', label: 'Review', icon: FileSearch },
  { to: '/admin/invites', label: 'Invites', icon: Mail, adminOnly: true },
];

function AdminLayout() {
  const { loading, profile } = useRequireRole(['admin', 'moderator']);
  const isAdmin = profile?.role === 'admin';
  // Moderators see the Editorial group + Audit log; People + Menu are filtered
  // out (and those routes 403 directly).
  const roleGroups = isAdmin
    ? sidebarGroups
    : sidebarGroups
        .map((g) => ({ ...g, items: g.items.filter((i) => !('adminOnly' in i && i.adminOnly)) }))
        .filter((g) => g.items.length > 0);
  // Role-aware chrome (Spec 27): the cross-surface "Manage" group (Creator
  // studio / Your home; the Admin link self-omits while in /admin).
  const cross = crossSurfaceLinks(profile, 'admin');
  const groups = cross.length ? [...roleGroups, { label: 'Manage', items: cross }] : roleGroups;
  const bottomNav = isAdmin
    ? bottomNavItems
    : bottomNavItems.filter((i) => !('adminOnly' in i && i.adminOnly));
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The production zone (studio + shows management) renders on the dark video
  // surface; the rest of admin stays editorial.
  const surface =
    pathname.startsWith('/admin/studio') || pathname.startsWith('/admin/shows')
      ? 'video'
      : 'editorial';
  if (loading) return null;
  return (
    <AppShell
      pageTitle="Admin"
      sidebarGroups={groups}
      bottomNavItems={bottomNav}
      homeHref="/admin"
      sidebarAccentClass="border-l-2 border-slate"
      exitLink={{ to: '/subscriber', label: 'Home' }}
      surface={surface}
    >
      <Outlet />
    </AppShell>
  );
}
